import { Alert } from '@eleo-rh/core-ui'
import { Modal } from 'antd'
import { useI18n } from 'context/I18nProvider'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import Marquee from 'react-fast-marquee'
import { useModal } from 'react-modal-hook'
import type { Settings } from 'shared/types'

type TopMessageMode = 'static' | 'marquee' | 'truncated'

interface TopMessageProps {
  settings: Settings
}

const TOP_MESSAGE_CLOSED_KEY = 'topMessageClosed'
const trailingSpaces = '\u00A0\u00A0\u00A0\u00A0'

function TopMessage({ settings }: TopMessageProps) {
  const { i18n } = useI18n()
  const topMessageContainerRef = useRef<HTMLDivElement | null>(null)
  const topMessageMeasureRef = useRef<HTMLSpanElement | null>(null)
  const [topMessageMode, setTopMessageMode] = useState<TopMessageMode>('static')
  const [showTopMessage, setShowTopMessage] = useState(() => {
    return localStorage.getItem(TOP_MESSAGE_CLOSED_KEY) !== 'true'
  })
  const topMessageText = useMemo(() => {
    if (!settings.topMessage) {
      return ''
    }
    const doc = new DOMParser().parseFromString(settings.topMessage, 'text/html')
    return doc.body.textContent?.trim() || ''
  }, [settings.topMessage])
  const shouldMarqueeTopMessage = topMessageMode === 'marquee'
  const shouldShowTopMessageViewMore = topMessageMode === 'truncated'
  const [showTopMessageModal, hideTopMessageModal] = useModal(
    () => (
      <Modal
        title={<span className="text-primary-800">{i18n('label.topMessage')}</span>}
        footer={null}
        onCancel={hideTopMessageModal}
        open
      >
        <div
          className="w-full text-justify [&_p]:text-justify [&_li]:text-justify"
          dangerouslySetInnerHTML={{ __html: settings.topMessage || '' }}
        />
      </Modal>
    ),
    [settings.topMessage, i18n],
  )

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key !== TOP_MESSAGE_CLOSED_KEY) {
        return
      }
      setShowTopMessage(event.newValue !== 'true')
    }
    window.addEventListener('storage', handleStorageChange)
    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [])

  useLayoutEffect(() => {
    const container = topMessageContainerRef.current
    const measure = topMessageMeasureRef.current

    if (!settings.topMessageEnabled || !showTopMessage || !topMessageText || !container || !measure) {
      setTopMessageMode('static')
      return
    }

    const computeTopMessageMode = () => {
      const containerWidth = container.clientWidth
      const textWidth = measure.scrollWidth

      if (!containerWidth || !textWidth) {
        setTopMessageMode('static')
        return
      }

      if (textWidth <= containerWidth) {
        setTopMessageMode('static')
        return
      }

      if (textWidth <= containerWidth * 2) {
        setTopMessageMode('marquee')
        return
      }

      setTopMessageMode('truncated')
    }

    computeTopMessageMode()

    const resizeObserver = new ResizeObserver(computeTopMessageMode)
    resizeObserver.observe(container)

    return () => resizeObserver.disconnect()
  }, [settings.topMessageEnabled, showTopMessage, topMessageText])

  const handleCloseTopMessage = () => {
    localStorage.setItem(TOP_MESSAGE_CLOSED_KEY, 'true')
    setShowTopMessage(false)
  }

  return settings.topMessageEnabled && showTopMessage ? (
    <div className="mr-12 flex items-center gap-2 overflow-hidden">
      <div className="flex-none w-[26vw] overflow-hidden">
        <Alert.Inline
          title={
            <div ref={topMessageContainerRef} className="relative w-full overflow-hidden">
              <span ref={topMessageMeasureRef} className="invisible absolute whitespace-nowrap pointer-events-none">
                {topMessageText}
              </span>
              {shouldMarqueeTopMessage ? (
                <Marquee pauseOnHover gradient={false} delay={2}>
                  {topMessageText}
                  {trailingSpaces}
                </Marquee>
              ) : (
                <span className="block truncate">{topMessageText}</span>
              )}
            </div>
          }
          closable
          onClose={handleCloseTopMessage}
        />
      </div>
      {shouldShowTopMessageViewMore && (
        <button
          type="button"
          className="flex-none bg-transparent border-0 p-0 cursor-pointer text-primary-800 text-sm underline! font-semibold"
          onClick={showTopMessageModal}
        >
          {i18n('label.viewMore')}
        </button>
      )}
    </div>
  ) : null
}

export default TopMessage
